import React, { useRef, useState } from 'react';
import { Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '../../services/api/uploads';

interface Props {
  value?: string;
  onUploaded: (url: string) => void;
  label?: string;
  testId?: string;
}

export function ImageUpload({ value, onUploaded, label = 'Unggah Gambar', testId }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Format tidak didukung', { description: 'Hanya JPG, PNG, WEBP.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran terlalu besar', { description: 'Maksimal 5MB.' });
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      onUploaded(url);
      toast.success('Gambar terunggah');
    } catch (e: any) {
      toast.error('Gagal mengunggah', { description: e.response?.data?.detail || e.message });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
      />
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
          <div className="flex gap-2">
            <button type="button" data-testid={testId ? `${testId}-replace` : undefined} onClick={() => inputRef.current?.click()} disabled={uploading} className="text-xs border border-white/15 text-white/70 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
              Ganti
            </button>
            <button type="button" data-testid={testId ? `${testId}-remove` : undefined} onClick={() => onUploaded('')} className="text-xs border border-red-400/30 text-red-300 px-3 py-1.5 rounded-lg hover:bg-red-400/10 transition-colors flex items-center gap-1">
              <X className="w-3 h-3" strokeWidth={2} /> Hapus
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={testId ? `${testId}-button` : undefined}
          className="w-full flex items-center justify-center gap-2 bg-[#050914] border border-dashed border-white/20 rounded-lg px-3 py-3 text-sm text-white/60 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Upload className="w-4 h-4" strokeWidth={1.8} />}
          {uploading ? 'Mengunggah...' : label}
        </button>
      )}
    </div>
  );
}
